import React from "react";
import { Card, CardContent } from "../ui/card";
import { Send, Eye, FileText, CheckCircle2, Flame } from "lucide-react";
import { KPICard } from "./KPICard";
import { SectionHeader } from "./HelperText";
import EmailTrendChart from "./EmailTrendChart";
import EmailFunnelCard from "./EmailFunnelCard";
import EmailBroadcastTable from "./EmailBroadcastTable";
import { useEmailPerformanceData } from "../../hooks/useEmailPerformanceData";

// Checkpoint 3.2: same acquisition-outcome KPI set as Overview (Delivered,
// Landing Page Views, Quote Starts, Quote Completes, High Intent Actions).
// Open Rate / Click Rate stay out of the primary Performance surface —
// DASH-EMAIL-P1-IMPL-008 §8/§9 explicitly prohibit both (machine-open
// safety and an uncharacterized click bot/scanner classification gap).
const KPI_DEFS = [
  { key: "delivered", title: "Delivered", format: "number", icon: Send },
  { key: "landing_page_views", title: "Landing Page Views", format: "number", icon: Eye },
  { key: "quote_starts", title: "Quote Starts", format: "number", icon: FileText },
  { key: "quote_completes", title: "Quote Completes", format: "number", icon: CheckCircle2 },
  { key: "high_intent_actions", title: "High Intent Actions", format: "number", icon: Flame },
];

function PerformanceKpiGridSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="performance-kpi-skeleton">
      {KPI_DEFS.map((def) => (
        <Card key={def.key} className="glass border-white/10 animate-pulse">
          <CardContent className="p-4 space-y-3">
            <div className="h-3 w-20 rounded bg-white/10" />
            <div className="h-6 w-16 rounded bg-white/10" />
            <div className="h-3 w-24 rounded bg-white/5" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * EmailPerformanceView — the Email tab's "Performance" view (Checkpoint 3.2).
 *
 * Answers "is our real acquisition funnel improving or breaking down over
 * the selected reporting Period?" via three sections: Performance Summary,
 * Performance Over Time (honestly unavailable — no time-series source
 * exists yet), and the Conversion Funnel (reusing EmailFunnelCard
 * unmodified).
 *
 * (Historical note, DASH-EMAIL-P1-IMPL-008/CHAR-007): this view previously
 * retired a fabricated per-Broadcast comparison (a fictional `brd-*`
 * catalog with hand-typed counts) and, having no authoritative per-
 * Broadcast breakdown, accepted only `period`/`customRange` -- Environment/
 * Program/Campaign/Broadcast/Audience/State were not accepted at all.
 *
 * DASH-EMAIL-P1-IMPL-032: #350/#351 removed the EDT/LPE filtering
 * asymmetry that justified the Period-only restriction (real Delivered
 * and real LPE funnel stages can now both be scoped by the SAME seven
 * governed filters, consistently). This view now accepts the identical
 * filter set Overview does, mapped identically (see
 * useEmailDashboardData.js's own header comment for the exact per-domain
 * Environment mapping) -- see EmailFilterBar.jsx's own `performance`
 * field config. EmailBroadcastTable below remains unaffected (it accepts
 * no props and unconditionally renders "not yet available" -- a real
 * per-Broadcast breakdown is still a separate, later checkpoint). No
 * Opens/Clicks/rate is introduced by this change.
 */
export default function EmailPerformanceView({
  period,
  customRange,
  environment,
  governedBroadcastId,
  governedAudienceId,
  governedProgramId,
  governedCampaignId,
  governedState,
}) {
  const { loading, error, summary, funnel } = useEmailPerformanceData({
    period,
    customRange,
    environment,
    governedBroadcastId: governedBroadcastId === "all" ? null : governedBroadcastId,
    governedAudienceId: governedAudienceId === "all" ? null : governedAudienceId,
    governedProgramId: governedProgramId === "all" ? null : governedProgramId,
    governedCampaignId: governedCampaignId === "all" ? null : governedCampaignId,
    governedState: governedState === "all" ? null : governedState,
  });

  const hasSummary = summary && Object.keys(summary).length > 0;

  return (
    <div className="space-y-6">
      {error && (
        <Card className="glass border-red-500/20" data-testid="email-performance-error">
          <CardContent className="p-4 text-sm text-red-300">
            Failed to load Email performance data: {error}
          </CardContent>
        </Card>
      )}

      {!error && loading && <PerformanceKpiGridSkeleton />}

      {!error && !loading && hasSummary && (
        <>
          <div>
            <SectionHeader
              title="Performance Summary"
              helper="Real totals for the selected Period, Environment, Broadcast, Audience, Program, Campaign, and State selection."
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="performance-kpi-grid">
              {KPI_DEFS.map((def) => {
                const metric = summary[def.key];
                return (
                  <KPICard
                    key={def.key}
                    title={def.title}
                    value={metric?.value}
                    format={def.format}
                    status={metric?.status}
                    reason={metric?.reason}
                    icon={def.icon}
                  />
                );
              })}
            </div>
          </div>

          <EmailTrendChart trend={[]} />

          <EmailFunnelCard funnel={funnel} />

          <EmailBroadcastTable />
        </>
      )}

      {!error && !loading && !hasSummary && (
        <Card className="glass border-white/10" data-testid="email-performance-empty">
          <CardContent className="p-8 text-center text-slate-400">
            <p className="text-lg">No Performance data yet</p>
            <p className="text-sm mt-2">
              Metrics will appear here once Email data is available for this selection.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
